import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Plus, Check, RotateCcw, Trash2, Eye, X,
    CalendarClock, ClipboardList, Megaphone, BellRing } from 'lucide-react';
import { alertAPI, utentiAPI } from '../services/api';
import { useRuoli } from '../utils/ruoli';
import DestinatarioSelect from '../components/DestinatarioSelect';
import { vaiA, haDestinazione, ETICHETTA } from '../utils/destinazione';
import IntestazioneSezione from '../components/comuni/IntestazioneSezione';
import { useAuthStore } from '../store/useStore';
import '../styles/Promemoria.css';

/**
 * Promemoria — promemoria dei pazienti, compiti dello staff e avvisi.
 *
 * Tre cose diverse nello stesso elenco, perche' chi apre questa
 * schermata vuole sapere cosa deve fare oggi, non consultare tre liste
 * separate.
 *
 * Chi non e' amministratore o segreteria vede solo cio' che lo
 * riguarda: il filtro vero e' nel backend, qui si sceglie soltanto
 * come guardarlo.
 */

const TIPI = {
    promemoria: { etichetta: 'Promemoria paziente', Icona: CalendarClock },
    compito:    { etichetta: 'Compito',             Icona: ClipboardList },
    avviso:     { etichetta: 'Avviso',              Icona: Megaphone },
};

/* I ruoli arrivano da /api/ruoli, non da un elenco scritto qui: vedi
   utils/ruoli.js. Fino al 21 settembre 2026 qui mancava
   `medico_senior`, e tre medici senior non ricevevano i promemoria di
   reparto senza che nessuno lo sapesse. */

const messaggioErrore = (e, ripiego) => e?.message || e?.response?.data?.message || ripiego;

const dataOra = (v) =>
    v ? new Date(v).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : '—';

/** Scaduto, oggi, o futuro: cambia quanto la voce urge. */
const quando = (v) => {
    if (!v) return '';
    const d = new Date(v), ora = new Date();
    const oggi = d.toDateString() === ora.toDateString();
    if (d < ora && !oggi) return 'scaduto';
    if (oggi) return 'oggi';
    return 'futuro';
};

const FORM_VUOTO = {
    tipo: 'compito', titolo: '', descrizione: '',
    data_ora: '', priorita: 'normale',
    destinatario: '',   // 'u:12' persona, 'r:segreteria' gruppo, '' tutti
};

const Promemoria = () => {
    const { ruoli: RUOLI } = useRuoli();
    const [voci, setVoci] = useState([]);
    const [utenti, setUtenti] = useState([]);
    const [filtroTipo, setFiltroTipo] = useState('');
    const [mostraChiusi, setMostraChiusi] = useState(false);
    const [caricamento, setCaricamento] = useState(true);
    const [errore, setErrore] = useState(null);
    const [avviso, setAvviso] = useState(null);

    const [form, setForm] = useState(null);
    const [salvataggio, setSalvataggio] = useState(false);

    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const coordina = ['admin', 'segreteria'].includes(user?.ruolo);

    const carica = async () => {
        try {
            setCaricamento(true);
            const params = {};
            if (filtroTipo) params.tipo = filtroTipo;
            if (mostraChiusi) params.stato = 'completato';
            const r = await alertAPI.lista(params);
            setVoci(r.data?.data?.voci || []);
            setErrore(null);
        } catch (e) {
            setErrore(messaggioErrore(e, 'Impossibile caricare l\'elenco'));
        } finally {
            setCaricamento(false);
        }
    };

    useEffect(() => { carica(); /* eslint-disable-next-line */ }, [filtroTipo, mostraChiusi]);

    useEffect(() => {
        if (coordina) {
            utentiAPI.getAll().then((r) => setUtenti(r.data?.data?.utenti || [])).catch(() => {});
        }
    }, [coordina]);

    const salva = async () => {
        if (!form.titolo.trim() || !form.data_ora) {
            setErrore('Titolo e data sono obbligatori.');
            return;
        }
        setSalvataggio(true);
        setErrore(null);
        try {
            const [genere, valore] = form.destinatario ? form.destinatario.split(':') : ['', ''];
            await alertAPI.crea({
                tipo: form.tipo,
                titolo: form.titolo,
                descrizione: form.descrizione || null,
                data_ora: form.data_ora,
                priorita: form.priorita,
                assegnato_a: genere === 'u' ? parseInt(valore, 10) : null,
                assegnato_ruolo: genere === 'r' ? valore : null,
            });
            setForm(null);
            setAvviso('Voce creata.');
            carica();
        } catch (e) {
            setErrore(messaggioErrore(e, 'Errore nella creazione'));
        } finally {
            setSalvataggio(false);
        }
    };

    const azione = async (fn, messaggio) => {
        setErrore(null);
        try {
            await fn();
            if (messaggio) setAvviso(messaggio);
            carica();
        } catch (e) {
            setErrore(messaggioErrore(e, 'Operazione non riuscita'));
        }
    };

    const destinatarioDi = (v) => {
        if (v.assegnato_cognome) return `${v.assegnato_cognome} ${v.assegnato_nome || ''}`.trim();
        if (v.assegnato_ruolo) return RUOLI.find((r) => r.valore === v.assegnato_ruolo)?.etichetta || v.assegnato_ruolo;
        return 'Tutti';
    };

    return (
        <div className="prm-wrap">
            <IntestazioneSezione
                titolo="Promemoria e compiti"
                icona={<BellRing size={22} aria-hidden="true" />}
                sottotitolo="Promemoria legati ai pazienti, compiti da svolgere e avvisi per lo staff."
                azione={coordina && (
                    <button className="prm-btn-primario" onClick={() => { setForm({ ...FORM_VUOTO }); setErrore(null); }}>
                        <Plus size={15} strokeWidth={2} aria-hidden="true" />
                        Nuovo
                    </button>
                )}
            />

            <div className="prm-filtri" data-non-trattiene>
                <div className="prm-gruppo">
                    <button className={filtroTipo === '' ? 'attivo' : ''} onClick={() => setFiltroTipo('')}>Tutti</button>
                    {Object.entries(TIPI).map(([k, t]) => (
                        <button key={k} className={filtroTipo === k ? 'attivo' : ''} onClick={() => setFiltroTipo(k)}>
                            {t.etichetta}
                        </button>
                    ))}
                </div>
                <label className="prm-chiusi">
                    <input type="checkbox" checked={mostraChiusi} onChange={(e) => setMostraChiusi(e.target.checked)} />
                    <span>Mostra le voci chiuse</span>
                </label>
            </div>

            {errore && <div className="rm-messaggio rm-messaggio-errore prm-messaggio">{errore}</div>}
            {avviso && <div className="rm-messaggio rm-messaggio-conferma prm-messaggio">{avviso}</div>}

            {caricamento ? (
                <div className="prm-stato">Caricamento…</div>
            ) : voci.length === 0 ? (
                <div className="prm-vuoto">
                    <ClipboardList size={38} strokeWidth={1.2} aria-hidden="true" />
                    <p>{mostraChiusi ? 'Nessuna voce chiusa.' : 'Non c\'è nulla in sospeso.'}</p>
                </div>
            ) : (
                <div className="prm-elenco">
                    {voci.map((v) => {
                        const { Icona, etichetta } = TIPI[v.tipo] || TIPI.compito;
                        const stato = quando(v.data_ora);
                        const chiuso = v.stato === 'completato';

                        return (
                            <div key={v.id} className={`prm-voce prm-${v.tipo} ${chiuso ? 'prm-chiusa' : ''} ${v.priorita === 'alta' ? 'prm-alta' : ''}`}>
                                <div className="prm-voce-icona"><Icona size={17} strokeWidth={1.7} aria-hidden="true" /></div>

                                <div className="prm-voce-corpo">
                                    <div className="prm-voce-testa">
                                        <strong>{v.titolo}</strong>
                                        {v.priorita === 'alta' && !chiuso && (
                                            <span className="rm-stato rm-stato-errore">Urgente</span>
                                        )}
                                    </div>

                                    {v.descrizione && <p className="prm-descrizione">{v.descrizione}</p>}

                                    <div className="prm-meta">
                                        <span className={`prm-quando prm-quando-${stato}`}>{dataOra(v.data_ora)}</span>
                                        <span>{etichetta}</span>
                                        {v.paziente_id && (
                                            <button className="prm-link" onClick={() => vaiA(navigate, v)}
                                                    title={haDestinazione(v)
                                                        ? `Apri ${ETICHETTA[v.riferimento_tipo]}`
                                                        : 'Apri la scheda del paziente'}>
                                                {v.paziente_cognome} {v.paziente_nome}
                                                {haDestinazione(v) && (
                                                    <span className="prm-verso">
                                                        › {ETICHETTA[v.riferimento_tipo]}
                                                    </span>
                                                )}
                                            </button>
                                        )}
                                        {v.tipo !== 'promemoria' && <span>Per: {destinatarioDi(v)}</span>}
                                        {v.creato_da_cognome && <span>Da: {v.creato_da_cognome}</span>}
                                        {chiuso && (
                                            <span className="prm-chiusa-nota">
                                                Chiusa {dataOra(v.completato_il)}
                                                {v.completato_da_cognome ? ` da ${v.completato_da_cognome}` : ''}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="prm-voce-azioni">
                                    {v.tipo === 'avviso' && !v.letto_da_me && !chiuso && (
                                        <button className="prm-btn-piccolo"
                                                onClick={() => azione(() => alertAPI.segnaLetto(v.id), 'Presa visione registrata.')}>
                                            <Eye size={14} strokeWidth={1.8} aria-hidden="true" />
                                            Presa visione
                                        </button>
                                    )}
                                    {v.tipo === 'avviso' && v.letture > 0 && (
                                        <span className="prm-letture">{v.letture} letture</span>
                                    )}
                                    {!chiuso && v.tipo !== 'avviso' && (
                                        <button className="prm-btn-piccolo"
                                                onClick={() => azione(() => alertAPI.completa(v.id), 'Segnata come fatta.')}>
                                            <Check size={14} strokeWidth={2} aria-hidden="true" />
                                            Fatto
                                        </button>
                                    )}
                                    {chiuso && coordina && (
                                        <button className="prm-btn-piccolo"
                                                onClick={() => azione(() => alertAPI.riapri(v.id), 'Riaperta.')}>
                                            <RotateCcw size={14} strokeWidth={1.8} aria-hidden="true" />
                                            Riapri
                                        </button>
                                    )}
                                    {coordina && (
                                        <button className="prm-btn-piccolo prm-btn-pericolo"
                                                onClick={() => {
                                                    if (window.confirm(`Eliminare "${v.titolo}"?`))
                                                        azione(() => alertAPI.remove(v.id), 'Eliminata.');
                                                }}>
                                            <Trash2 size={14} strokeWidth={1.8} aria-hidden="true" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {form && (
                <div className="prm-overlay" onClick={() => !salvataggio && setForm(null)}>
                    <div className="prm-modale" onClick={(e) => e.stopPropagation()}>
                        <div className="prm-modale-testa">
                            <h2>Nuova voce</h2>
                            <button onClick={() => setForm(null)} aria-label="Chiudi">
                                <X size={17} strokeWidth={1.8} />
                            </button>
                        </div>

                        <div className="prm-campo">
                            <label>Tipo</label>
                            <div className="prm-gruppo">
                                <button className={form.tipo === 'compito' ? 'attivo' : ''}
                                        onClick={() => setForm({ ...form, tipo: 'compito' })}>Compito</button>
                                <button className={form.tipo === 'avviso' ? 'attivo' : ''}
                                        onClick={() => setForm({ ...form, tipo: 'avviso' })}>Avviso</button>
                            </div>
                            <small>
                                {form.tipo === 'compito'
                                    ? 'Qualcosa da fare: chi lo esegue lo segna come fatto, e vale per tutti.'
                                    : 'Una comunicazione: non si esegue, ognuno ne prende atto.'}
                            </small>
                        </div>

                        <div className="prm-campo">
                            <label htmlFor="prm-titolo">Titolo</label>
                            <input id="prm-titolo" type="text" value={form.titolo} autoFocus
                                   onChange={(e) => setForm({ ...form, titolo: e.target.value })}
                                   placeholder={form.tipo === 'compito' ? 'Es. ordinare siringhe' : 'Es. chiusura ambulatorio il 15 agosto'} />
                        </div>

                        <div className="prm-campo">
                            <label htmlFor="prm-desc">Dettagli</label>
                            <textarea id="prm-desc" rows={3} value={form.descrizione}
                                      onChange={(e) => setForm({ ...form, descrizione: e.target.value })}
                                      placeholder="Facoltativo" />
                        </div>

                        <div className="prm-riga">
                            <div className="prm-campo">
                                <label htmlFor="prm-data">Quando</label>
                                <input id="prm-data" type="datetime-local" value={form.data_ora}
                                       onChange={(e) => setForm({ ...form, data_ora: e.target.value })} />
                            </div>
                            <div className="prm-campo">
                                <label htmlFor="prm-prio">Priorità</label>
                                <select id="prm-prio" value={form.priorita}
                                        onChange={(e) => setForm({ ...form, priorita: e.target.value })}>
                                    <option value="bassa">Bassa</option>
                                    <option value="normale">Normale</option>
                                    <option value="alta">Urgente</option>
                                </select>
                            </div>
                        </div>

                        <div className="prm-campo">
                            <label htmlFor="prm-dest">A chi</label>
                            <DestinatarioSelect
                                inputId="prm-dest"
                                value={form.destinatario}
                                onChange={(v) => setForm({ ...form, destinatario: v })}
                                ruoli={RUOLI}
                                utenti={utenti}
                                vuoto="Tutti"
                            />
                            <small>
                                Un compito assegnato a un gruppo si chiude per tutti: se l'ordine è
                                stato fatto, è fatto per l'intera segreteria.
                            </small>
                        </div>

                        {errore && <div className="rm-messaggio rm-messaggio-errore">{errore}</div>}

                        <div className="prm-modale-azioni">
                            <button className="rm-btn" onClick={() => setForm(null)} disabled={salvataggio}>Annulla</button>
                            <button className="rm-btn rm-btn-primario" onClick={salva} disabled={salvataggio}>
                                {salvataggio ? 'Salvataggio…' : 'Crea'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Promemoria;
